<template>
  <div class="home">
    <div class="header">
      <h1 class="title">核对事项</h1>
    </div>

    <div class="content">
      <template v-if="checklistItems.length > 0">
        <div class="checklist-grid">
          <div 
            v-for="item in checklistItems" 
            :key="item.id"
            class="checklist-item"
            @click="goToDetail(item)"
            @touchstart="startLongPress(item)"
            @touchend="cancelLongPress"
            @touchmove="cancelLongPress"
          >
            <div class="item-content">
              <div class="item-header">
                <div class="title-wrapper">
                  <h3 class="item-title">{{ item.get('title') }}</h3>
                  <van-tag 
                    :type="isCompleted(item) ? 'success' : 'primary'"
                    size="small"
                    class="status-tag"
                  >
                    {{ isCompleted(item) ? '已完成' : '进行中' }}
                  </van-tag>
                </div>
              </div>
              <p class="item-desc" v-if="item.get('description')">
                {{ item.get('description') }}
              </p>
              <div class="progress-info" v-if="getStepsInfo(item).total > 0">
                <div class="progress-text">
                  <span>完成进度</span>
                  <span>{{ getStepsInfo(item).completed }}/{{ getStepsInfo(item).total }}</span>
                </div>
                <div class="progress-bar">
                  <div 
                    class="progress-fill"
                    :style="{ width: getStepsInfo(item).percentage + '%' }"
                  ></div>
                </div>
              </div>
              <div class="steps-preview" v-if="getStepsPreview(item).length">
                <div 
                  v-for="(step, index) in getStepsPreview(item)"
                  :key="index"
                  class="step-item"
                  :class="{ completed: step.completed }"
                >
                  <van-icon :name="step.completed ? 'checked' : 'circle'" />
                  <span class="step-text">{{ step.content }}</span>
                </div>
              </div>
              <div class="item-category" v-if="item.get('category')">
                <van-icon name="label-o" />
                <span>{{ item.get('category').get('name') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <div v-else class="empty-state">
        <van-empty
          class="custom-empty"
          image="search"
          description="还没有任何核对事项"
        >
          <template #bottom>
            <div class="empty-hint">
              <p>点击按钮创建您的第一个核对事项</p>
            </div>
          </template>
        </van-empty>
      </div>
    </div>

    <van-button
      type="primary"
      class="add-button"
      round
      icon="plus"
      @click="showCreateDialog"
    >
      新建事项
    </van-button>

    <van-dialog
      v-model:show="showDialog"
      title="新建核对事项"
      show-cancel-button
      @confirm="createItem"
      class="create-dialog"
    >
      <van-form @submit="createItem">
        <van-field
          v-model="newItem.title"
          name="title"
          label="标题"
          placeholder="请输入标题"
          :rules="[{ required: true, message: '请填写标题' }]"
        />
        <van-field
          v-model="newItem.description"
          name="description"
          label="描述"
          type="textarea"
          placeholder="请输入描述"
          rows="3"
          autosize
        />
        <van-field
          v-model="newItem.categoryName"
          is-link
          readonly
          name="category"
          label="分类"
          placeholder="点击选择分类"
          @click="showCategoryPicker"
        />
      </van-form>
    </van-dialog>

    <van-action-sheet
      v-model:show="showPicker"
      :actions="categoryActions"
      @select="onCategorySelect"
      cancel-text="取消"
      title="选择分类"
      class="category-sheet"
    />

    <van-dialog
      v-model:show="showDeleteDialog"
      title="删除事项"
      message="确定要删除这个事项吗？"
      show-cancel-button
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AV from 'leancloud-storage'
import { showToast } from 'vant'

export default {
  name: 'Home',
  setup() {
    const store = useStore()
    const router = useRouter()
    const loading = ref(false)
    const finished = ref(false)
    const refreshing = ref(false)
    const showDialog = ref(false)
    const showPicker = ref(false)
    const showDeleteDialog = ref(false)
    const selectedItem = ref(null)
    let longPressTimer = null
    
    const newItem = ref({
      title: '',
      description: '',
      category: null,
      categoryName: ''
    })

    const checklistItems = computed(() => store.state.checklistItems)
    const categories = computed(() => store.state.categories)
    
    const categoryActions = computed(() => {
      const options = categories.value.map(cat => ({
        name: cat.get('name'),
        value: cat.id
      }))
      options.push({ name: '无分类', value: null })
      return options
    })

    const showCategoryPicker = async () => {
      if (categories.value.length === 0) {
        await store.dispatch('fetchCategories')
      }
      showPicker.value = true
    }

    const onCategorySelect = (action) => {
      newItem.value.category = action.value
      newItem.value.categoryName = action.name
      showPicker.value = false
    }

    const onLoad = async () => {
      await store.dispatch('fetchChecklistItems')
      loading.value = false
      finished.value = true
    }

    const onRefresh = async () => {
      await store.dispatch('fetchChecklistItems')
      refreshing.value = false
    }

    const showCreateDialog = () => {
      showDialog.value = true
    }

    const createItem = async () => {
      if (!newItem.value.title) return
      
      const ChecklistItem = AV.Object.extend('ChecklistItem')
      const item = new ChecklistItem()
      
      const itemData = {
        title: newItem.value.title,
        description: newItem.value.description || '',
        user: AV.User.current(),
        completed: false,
        steps: [],
        collaborators: [],
        shareable: false
      }

      if (newItem.value.category !== null) {
        try {
          const Category = AV.Object.createWithoutData('Category', newItem.value.category)
          itemData.category = Category
        } catch (error) {
          console.error('创建分类引用失败:', error)
        }
      }

      try {
        item.set(itemData)
        await item.save()
        showToast('创建成功')
        await store.dispatch('fetchChecklistItems')
        showDialog.value = false
        newItem.value = {
          title: '',
          description: '',
          category: null,
          categoryName: ''
        }
      } catch (error) {
        console.error('创建失败:', error)
        showToast('创建失败: ' + error.message)
      }
    }

    const goToDetail = (item) => {
      router.push(`/item/${item.id}`)
    }

    const toggleComplete = async (item) => {
      item.set('completed', !item.get('completed'))
      try {
        await item.save()
        await store.dispatch('fetchChecklistItems')
      } catch (error) {
        showToast('操作失败')
      }
    }

    const getStepsInfo = (item) => {
      const steps = item.get('steps') || []
      const total = steps.length
      const completed = steps.filter(step => step.completed).length
      const percentage = total > 0 ? (completed / total) * 100 : 0
      return { total, completed, percentage }
    }

    const getStepsPreview = (item) => {
      const steps = item.get('steps') || []
      return steps.slice(0, 3).map(step => ({
        content: step.content,
        completed: step.completed
      }))
    }

    const isCompleted = (item) => {
      const steps = item.get('steps') || []
      if (steps.length === 0) return false
      return steps.every(step => step.completed)
    }

    onMounted(async () => {
      try {
        await store.dispatch('fetchCategories')
        await store.dispatch('fetchChecklistItems')
      } catch (error) {
        showToast('加载失败')
      }
    })

    const onScroll = async (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target
      if (scrollHeight - scrollTop - clientHeight < 50) {
        // 这里可以添加加载更多的逻辑
      }
    }

    // 开始长按
    const startLongPress = (item) => {
      longPressTimer = setTimeout(() => {
        selectedItem.value = item
        showDeleteDialog.value = true
      }, 800) // 800ms 的长按时间
    }

    // 取消长按
    const cancelLongPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer)
        longPressTimer = null
      }
    }

    // 确认删除
    const confirmDelete = async () => {
      if (!selectedItem.value) return

      try {
        await selectedItem.value.destroy()
        await store.dispatch('fetchChecklistItems')
        showToast('删除成功')
      } catch (error) {
        showToast('删除失败')
      }
      selectedItem.value = null
    }

    // 组件卸载时清理定时器
    onUnmounted(() => {
      if (longPressTimer) {
        clearTimeout(longPressTimer)
      }
    })

    return {
      loading,
      finished,
      refreshing,
      showDialog,
      showPicker,
      newItem,
      checklistItems,
      categoryActions,
      onLoad,
      onRefresh,
      showCreateDialog,
      createItem,
      goToDetail,
      toggleComplete,
      getStepsInfo,
      getStepsPreview,
      onCategorySelect,
      showCategoryPicker,
      isCompleted,
      onScroll,
      showDeleteDialog,
      selectedItem,
      startLongPress,
      cancelLongPress,
      confirmDelete
    }
  }
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  height: 100vh;
  background-color: #f5f5f7;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: rgba(245, 245, 247, 0.9);
  backdrop-filter: blur(20px);
  padding: 16px 20px;
  flex-shrink: 0;
}

.title {
  font-size: 34px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 100px);
}

.checklist-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.checklist-item {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  height: auto;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  user-select: none; /* 防止长按选中文本 */
  -webkit-touch-callout: none; /* 禁用iOS长按菜单 */
}

.checklist-item:active {
  transform: scale(0.98);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.item-content {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.item-header {
  margin-bottom: 12px;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-title {
  font-size: 15px;
  line-height: 1.3;
  margin-bottom: 4px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.item-desc {
  font-size: 12px;
  color: #86868b;
  margin: 4px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.progress-info {
  margin: 8px 0;
}

.progress-text {
  display: flex;
  justify-content: space-between;
  color: #86868b;
  font-size: 12px;
  margin-bottom: 4px;
}

.progress-bar {
  height: 3px;
  background: #f5f5f7;
  border-radius: 1.5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #0071e3;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.steps-preview {
  margin: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 72px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.4;
  padding: 4px 0;
}

.step-item.completed {
  color: #86868b;
}

.step-item.completed .van-icon {
  color: #10b981;
}

.step-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
}

.item-category {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f5f5f7;
  font-size: 12px;
  color: #86868b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.add-button {
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom) + 70px);
  right: 20px;
  z-index: 999;
  width: auto;
  padding: 0 24px;
  height: 44px;
  font-size: 16px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

@supports not (padding: env(safe-area-inset-bottom)) {
  .add-button {
    bottom: 80px;
  }
}

@media screen and (max-height: 667px) {
  .add-button {
    bottom: 65px;
  }
}

:deep(.create-dialog .van-dialog__header) {
  padding: 20px 0;
  font-weight: 600;
  font-size: 18px;
}

:deep(.create-dialog .van-field) {
  padding: 16px;
}

:deep(.category-sheet .van-action-sheet__header) {
  font-size: 16px;
  font-weight: 600;
}

:deep(.van-tag--medium) {
  padding: 2px 8px;
  font-size: 10px;
}

.status-tag {
  flex-shrink: 0;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
}

:deep(.van-tag--primary) {
  background: rgba(0, 122, 255, 0.1);
  color: #007AFF;
  border: none;
}

:deep(.van-tag--success) {
  background: rgba(52, 199, 89, 0.1);
  color: #34C759;
  border: none;
}

@media screen and (max-height: 667px) {
  .header {
    padding: 12px 16px;
  }
  
  .title {
    font-size: 28px;
  }
  
  .checklist-grid {
    gap: 8px;
  }
  
  .item-content {
    padding: 10px;
  }
}

@supports (padding: max(0px)) {
  .content {
    padding-bottom: max(100px, calc(100px + env(safe-area-inset-bottom)));
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 200px);
  padding: 20px;
}

.custom-empty {
  padding: 32px 0;
}

.empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
}

.empty-hint p {
  color: #86868b;
  font-size: 14px;
  margin: 0;
}

:deep(.van-empty__image) {
  width: 120px;
  height: 120px;
}

:deep(.van-empty__description) {
  color: #1d1d1f;
  font-size: 16px;
  font-weight: 500;
  margin: 16px 0;
}

:deep(.van-dialog) {
  border-radius: 14px;
}

:deep(.van-dialog__header) {
  padding: 20px 24px 0;
  font-weight: 600;
  font-size: 17px;
}

:deep(.van-dialog__message) {
  padding: 16px 24px;
  font-size: 15px;
}

:deep(.van-dialog__footer) {
  padding: 8px 12px 20px;
}

:deep(.van-button--danger) {
  color: #ff3b30;
}
</style> 